/**
 * ComparisonView page for side-by-side creative comparison
 * Displays 2-5 selected creatives with their analysis results
 */

import {
  ArrowLeft,
  AlertCircle,
  Loader2,
  GitCompare,
  Sparkles,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ComparisonReport, ComparisonTable } from '@/components/comparison';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { useToast } from '@/components/ui/Toast';
import {
  useComparison,
  useGenerateComparisonReport,
  validateComparisonSelection,
} from '@/hooks/useComparison';

// ============================================================================
// LOADING STATE
// ============================================================================

function LoadingState() {
  return (
    <div className="flex flex-col items-center justify-center py-20">
      <Loader2 className="h-10 w-10 text-brand animate-spin mb-4" />
      <h2 className="text-lg font-semibold text-primary mb-2">
        Loading creatives...
      </h2>
      <p className="text-sm text-secondary">
        Fetching analysis data for comparison
      </p>
    </div>
  );
}

// ============================================================================
// ERROR STATE
// ============================================================================

interface ErrorStateProps {
  message: string;
  onBack: () => void;
}

function ErrorState({ message, onBack }: ErrorStateProps) {
  return (
    <Card className="text-center py-12">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-xl bg-danger-soft mb-4">
        <AlertCircle className="h-8 w-8 text-danger" />
      </div>
      <h2 className="text-lg font-semibold text-primary mb-2">
        Unable to Compare
      </h2>
      <p className="text-sm text-secondary mb-6 max-w-sm mx-auto">{message}</p>
      <Button variant="secondary" onClick={onBack}>
        Go Back
      </Button>
    </Card>
  );
}

// ============================================================================
// EMPTY STATE
// ============================================================================

interface EmptyStateProps {
  onBack: () => void;
}

function EmptyState({ onBack }: EmptyStateProps) {
  return (
    <Card className="text-center py-12">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-xl bg-surface-subtle mb-4">
        <GitCompare className="h-8 w-8 text-muted" />
      </div>
      <h2 className="text-lg font-semibold text-primary mb-2">
        No Creatives Selected
      </h2>
      <p className="text-sm text-secondary mb-6 max-w-sm mx-auto">
        Select 2-5 creatives from the project dashboard to compare their
        analysis results side-by-side.
      </p>
      <Button variant="primary" onClick={onBack}>
        Go to Projects
      </Button>
    </Card>
  );
}

// ============================================================================
// PAGE HEADER
// ============================================================================

interface PageHeaderProps {
  count: number;
  onBack: () => void;
}

function PageHeader({ count, onBack }: PageHeaderProps) {
  return (
    <div className="flex items-center justify-between mb-6">
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="p-2 rounded-lg hover:bg-surface-subtle transition-colors"
          aria-label="Go back"
        >
          <ArrowLeft className="h-5 w-5 text-secondary" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-primary">Compare Creatives</h1>
          <p className="text-sm text-secondary">
            {count} creative{count !== 1 ? 's' : ''} selected
          </p>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// ANALYSIS WARNING
// ============================================================================

interface AnalysisWarningProps {
  count: number;
}

function AnalysisWarning({ count }: AnalysisWarningProps) {
  if (count === 0) {
    return null;
  }

  return (
    <div className="mb-6 p-4 bg-warning-soft border border-warning/20 rounded-lg flex items-start gap-3">
      <AlertCircle className="h-5 w-5 text-warning flex-shrink-0 mt-0.5" />
      <div>
        <h3 className="text-sm font-semibold text-primary">
          Some creatives not analyzed
        </h3>
        <p className="text-sm text-secondary mt-1">
          {count} of the selected creatives {count === 1 ? "hasn't" : "haven't"}{' '}
          been analyzed yet. Analyze them to see complete comparison data.
        </p>
      </div>
    </div>
  );
}

// ============================================================================
// COMPARISON VIEW PAGE
// ============================================================================

export function ComparisonView() {
  const navigate = useNavigate();
  const { creativeIds, data, isLoading, isError, errors } = useComparison();
  const generateReport = useGenerateComparisonReport();
  const { error: showError } = useToast();
  const [report, setReport] = useState<{
    text: string;
    count: number;
  } | null>(null);

  // Validate selection
  const validation = validateComparisonSelection(creativeIds.length);

  // Handle back navigation
  const handleBack = () => {
    navigate(-1);
  };

  // Handle going to projects
  const handleGoToProjects = () => {
    navigate('/projects');
  };

  // Check if all creatives have completed analyses
  const allAnalyzed = data.length > 0 && data.every((item) => item.analysis);

  // Get project ID from first creative (all should be from same project)
  const projectId = data[0]?.creative.projectId;

  // Handle generate report
  const handleGenerateReport = () => {
    if (!projectId || !allAnalyzed) return;

    generateReport.mutate(
      { projectId, creativeIds },
      {
        onSuccess: (response) => {
          setReport({
            text: response.report,
            count: response.creativeCount,
          });
        },
        onError: (err) => {
          showError(
            'Failed to generate report',
            err instanceof Error ? err.message : 'Please try again'
          );
        },
      }
    );
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="p-8 max-w-7xl mx-auto">
        <PageHeader count={creativeIds.length} onBack={handleBack} />
        <LoadingState />
      </div>
    );
  }

  // Empty state - no IDs provided
  if (creativeIds.length === 0) {
    return (
      <div className="p-8 max-w-7xl mx-auto">
        <PageHeader count={0} onBack={handleBack} />
        <EmptyState onBack={handleGoToProjects} />
      </div>
    );
  }

  // Validation error
  if (!validation.valid) {
    return (
      <div className="p-8 max-w-7xl mx-auto">
        <PageHeader count={creativeIds.length} onBack={handleBack} />
        <ErrorState
          message={validation.error || 'Invalid selection'}
          onBack={handleBack}
        />
      </div>
    );
  }

  // Error state
  if (isError) {
    const firstError = errors.find((e) => e !== null);
    return (
      <div className="p-8 max-w-7xl mx-auto">
        <PageHeader count={creativeIds.length} onBack={handleBack} />
        <ErrorState
          message={firstError?.message || 'Failed to load creatives'}
          onBack={handleBack}
        />
      </div>
    );
  }

  // No data loaded
  if (data.length === 0) {
    return (
      <div className="p-8 max-w-7xl mx-auto">
        <PageHeader count={creativeIds.length} onBack={handleBack} />
        <ErrorState
          message="Could not load the selected creatives"
          onBack={handleBack}
        />
      </div>
    );
  }

  // Validate same media type (enforce at URL-level too)
  const mediaTypes = data.map((item) => item.creative.mediaType || 'video');
  const uniqueMediaTypes = [...new Set(mediaTypes)];
  const isMixedTypes = uniqueMediaTypes.length > 1;

  // Count creatives without analysis
  const notAnalyzedCount = data.filter((item) => !item.analysis).length;

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <PageHeader count={data.length} onBack={handleBack} />

      {/* Warning for mixed media types */}
      {isMixedTypes && (
        <div className="mb-6 p-4 bg-warning-soft border border-warning/20 rounded-lg flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-warning flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-primary">
              Mixed media types
            </h3>
            <p className="text-sm text-secondary mt-1">
              This comparison contains both video and image creatives. For best results,
              compare creatives of the same media type.
            </p>
          </div>
        </div>
      )}

      {/* Warning for unanalyzed creatives */}
      <AnalysisWarning count={notAnalyzedCount} />

      {/* Comparison table */}
      <ComparisonTable items={data} />

      {/* Generate Report Section */}
      <div className="mt-6">
        {report ? (
          <ComparisonReport
            report={report.text}
            creativeCount={report.count}
          />
        ) : (
          <Card className="p-6 text-center">
            <div className="flex flex-col items-center gap-4">
              <div className="p-3 bg-brand/10 rounded-xl">
                <Sparkles className="h-6 w-6 text-brand" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-primary mb-1">
                  AI Comparison Report
                </h3>
                <p className="text-sm text-secondary max-w-md mx-auto">
                  {allAnalyzed
                    ? 'Generate a detailed comparison narrative that highlights key differences between your creatives.'
                    : 'All selected creatives must have completed analyses to generate a comparison report.'}
                </p>
              </div>
              <Button
                variant="primary"
                onClick={handleGenerateReport}
                disabled={!allAnalyzed || generateReport.isPending}
              >
                {generateReport.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Generate Report
                  </>
                )}
              </Button>
            </div>
          </Card>
        )}
      </div>

      {/* Footer actions */}
      <div className="mt-6 flex items-center justify-between">
        <Button variant="ghost" onClick={handleBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Project
        </Button>
        {report && (
          <Button
            variant="secondary"
            onClick={() => setReport(null)}
          >
            Clear Report
          </Button>
        )}
      </div>
    </div>
  );
}
